import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import useAuth from './hooks/useAuth';
import CollegeLayout from './components/layout/CollegeLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StudentRosterPage from './pages/StudentRosterPage';
import CohortManagementPage from './pages/CohortManagementPage';
import CreditTrackerPage from './pages/CreditTrackerPage';
import ReportsPage from './pages/ReportsPage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { ROUTE_PERMISSIONS, BILLING_ADMIN } from './utils/constants';

function FullScreenSpinner() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, isCollegeUser, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!isAuthenticated || !isCollegeUser) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isCollegeUser, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (isAuthenticated && isCollegeUser) return <Navigate to="/" replace />;
  return children;
}

// Role-aware wrapper. Routes the user away with an inline error if they don't
// have the right role (or required flag) for this page.
function RoleRoute({ children }) {
  const { role, isBillingAdmin, isAdmin } = useAuth();
  const location = useLocation();
  const matchedKey = Object.keys(ROUTE_PERMISSIONS).find((k) =>
    k === '/' ? location.pathname === '/' : location.pathname.startsWith(k)
  );
  const allowed = matchedKey ? ROUTE_PERMISSIONS[matchedKey] : null;

  if (!allowed) return children;

  // Flag-based check: BILLING_ADMIN is not a role, it's a per-user grant.
  // Show a tailored message to college_admins who lack the flag, since they'd
  // otherwise expect access based on their role.
  if (allowed.includes(BILLING_ADMIN)) {
    if (isBillingAdmin) return children;
    if (isAdmin) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">
            <AlertTitle>Billing access not granted</AlertTitle>
            Billing information is restricted to your institution's designated billing contact.
            If you need access, contact your program administrator.
          </Alert>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Your role doesn't have access to this page. Contact your college coordinator if this seems wrong.
        </Alert>
      </Box>
    );
  }

  if (!role || !allowed.includes(role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Your role doesn't have access to this page. Contact your college coordinator if this seems wrong.
        </Alert>
      </Box>
    );
  }
  return children;
}

const routes = [
  { path: '/', element: <DashboardPage /> },
  { path: '/students', element: <StudentRosterPage /> },
  { path: '/cohorts', element: <CohortManagementPage /> },
  { path: '/credits', element: <CreditTrackerPage /> },
  { path: '/reports', element: <ReportsPage /> },
  { path: '/billing', element: <BillingPage /> },
  { path: '/settings', element: <SettingsPage /> },
];

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {routes.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={
            <ProtectedRoute>
              <CollegeLayout>
                <RoleRoute>{r.element}</RoleRoute>
              </CollegeLayout>
            </ProtectedRoute>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
